/**
 * central domain configuration
 * strictly 1 variable per value with zero duplicate variables and zero ||
 */

const getEnv = (key: string, fallback: string = ''): string => {
    try {
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            const raw = import.meta.env[key];
            if (typeof raw === 'string' && raw.trim() !== '') {
                return raw.trim();
            }
        }
    } catch {}
    return fallback;
};

// Clean domain helper (removes protocol, port, paths)
export const sanitizeDomain = (rawDomain?: string): string => {
    if (!rawDomain) return '';
    return rawDomain
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        .split(':')[0]
        .trim();
};

// Comma-separated domain list parser
export const parseDomainList = (raw?: string): string[] => {
    if (!raw || typeof raw !== 'string') return [];
    return raw
        .split(',')
        .map(d => sanitizeDomain(d))
        .filter(d => d.length > 0);
};

const getBrowserHostname = (): string => {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
        return window.location.hostname;
    }
    return 'localhost';
};

// Extract root domain from current host (e.g. "p.myco.com" -> "myco.com", "localhost" -> "localhost")
const deriveRootDomain = (): string => {
    const envRoot = getEnv('VITE_SAAS_ROOT', '');
    if (envRoot !== '') return sanitizeDomain(envRoot);

    const currentHost = getBrowserHostname();
    const isLocal = ['localhost', '127.0.0.1'].includes(currentHost) ? true : currentHost.includes(':');
    if (isLocal) {
        return currentHost;
    }

    // IP address check
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(currentHost)) {
        return currentHost;
    }

    // If host has 3+ parts (e.g. p.brand.com or marketplace.brand.com)
    const parts = currentHost.split('.');
    if (parts.length > 2) {
        // Drop known prefixes
        if (['p', 'marketplace', 'we', 'app', 'portal', 'www'].includes(parts[0])) {
            return parts.slice(1).join('.');
        }
    }
    return currentHost;
};

const rootDomain = deriveRootDomain();

// Standalone Domains Resolution (supports comma-separated domains)
const rawStandalone = getEnv('VITE_STUDIO_STANDALONE', '');
const rawStandaloneAlt = getEnv('VITE_STUDIO_STANDALONE_ALT', '');

const parsedStandaloneList = parseDomainList(rawStandalone);
const parsedAltList = parseDomainList(rawStandaloneAlt);

const defaultStandalone = rootDomain.includes('.') ? `we.${rootDomain}` : rootDomain;
const defaultAlt = rootDomain.includes('.') ? `www.we.${rootDomain}` : `www.${rootDomain}`;

const initialStandaloneDomains = Array.from(new Set([
    ...(parsedStandaloneList.length > 0 ? parsedStandaloneList : [defaultStandalone]),
    ...(parsedAltList.length > 0 ? parsedAltList : [defaultAlt])
]));

export const DOMAIN_CONFIG = {
    // 1. The Main SaaS Marketing Site / Admin Dashboard
    SAAS_ROOT: sanitizeDomain(getEnv('VITE_SAAS_ROOT', rootDomain)),
    
    // 2. The Base Domain for Tenant Portals (Subdomains)
    SAAS_PORTAL_BASE: sanitizeDomain(getEnv('VITE_SAAS_PORTAL_BASE', rootDomain.includes('.') ? `p.${rootDomain}` : rootDomain)),
    
    // 3. The Marketplace Directory
    SAAS_MARKETPLACE: sanitizeDomain(getEnv('VITE_SAAS_MARKETPLACE', rootDomain.includes('.') ? `marketplace.${rootDomain}` : rootDomain)),
    
    // 4. The Standalone Portfolio Domains (Single Tenant Mode - Supports comma-separated list)
    STUDIO_STANDALONE: parsedStandaloneList[0] || defaultStandalone,
    STUDIO_STANDALONE_ALT: parsedAltList[0] || (parsedStandaloneList[1] || defaultAlt),
    STANDALONE_DOMAINS: initialStandaloneDomains,

    // 5. The Fixed Tenant ID for the Standalone Portfolio
    STANDALONE_TENANT_ID: getEnv('VITE_STANDALONE_TENANT_ID', ''),

    // 6. Dynamic Contact, Privacy, Setup & Support Emails
    PRIVACY_EMAIL: getEnv('VITE_SYNCZ_PRIVACY_EMAIL', `privacy@${rootDomain.includes('.') ? rootDomain : 'localhost'}`),
    SETUP_EMAIL: getEnv('VITE_SYNCZ_SETUP_EMAIL', `setup@${rootDomain.includes('.') ? rootDomain : 'localhost'}`),
    SUPPORT_EMAIL: getEnv('VITE_SYNCZ_SUPPORT_EMAIL', `support@${rootDomain.includes('.') ? rootDomain : 'localhost'}`),

    // 7. Dynamic Visitor Tracking Config
    VI_TRACK_APP_ID: getEnv('VITE_SYNCZ_TRACK_APP_ID', 'syncz_app'),
    VI_TRACK_DOMAIN: sanitizeDomain(getEnv('VITE_SYNCZ_TRACK_DOMAIN', rootDomain)),
    VI_TRACK_MODE: getEnv('VITE_SYNCZ_TRACK_MODE', 'pixel')
};

// Derived Helpers
export const IS_LOCALHOST = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost'));

export const isSaaSPlatform = (hostname: string) => {
    const clean = sanitizeDomain(hostname).toLowerCase();
    const root = DOMAIN_CONFIG.SAAS_ROOT.toLowerCase();
    const market = DOMAIN_CONFIG.SAAS_MARKETPLACE.toLowerCase();
    return clean === `www.${root}` ||
           clean === root ||
           clean === market;
};

export const isStandalonePortfolio = (hostname: string): boolean => {
    if (!hostname) return false;
    const cleanHost = sanitizeDomain(hostname).toLowerCase();

    // 1. Direct match in configured standalone domains list (from comma-separated env vars)
    const inStandaloneList = DOMAIN_CONFIG.STANDALONE_DOMAINS.some(domain => {
        const cleanDomain = domain.toLowerCase();
        return cleanHost === cleanDomain ||
               cleanHost === `www.${cleanDomain}` ||
               `www.${cleanHost}` === cleanDomain;
    });

    if (inStandaloneList) return true;

    // 2. Primary / Alt matches
    if (cleanHost === DOMAIN_CONFIG.STUDIO_STANDALONE.toLowerCase() ||
        cleanHost === DOMAIN_CONFIG.STUDIO_STANDALONE_ALT.toLowerCase() ||
        cleanHost === `www.${DOMAIN_CONFIG.STUDIO_STANDALONE.toLowerCase()}`) {
        return true;
    }

    // 3. Localhost & Cloud environments
    return cleanHost.includes('run.app') ||
           cleanHost === 'localhost' ||
           cleanHost === '127.0.0.1';
};

